import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAdminData, authHeaders } from "@/hooks/useAdminData";
import { X, Edit, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "nuovo", label: "Nuovo", style: "bg-blue-100 text-blue-700" },
  { value: "in_valutazione", label: "In Valutazione", style: "bg-sky-100 text-sky-700" },
  { value: "in_lavorazione", label: "In Lavorazione", style: "bg-amber-100 text-amber-700" },
  { value: "preventivo_inviato", label: "Preventivo Inviato", style: "bg-purple-100 text-purple-700" },
  { value: "accettato", label: "Accettato", style: "bg-green-100 text-green-700" },
  { value: "rifiutato", label: "Rifiutato", style: "bg-red-100 text-red-700" },
];

const serviceLabels = { trasporto: "Trasporto", logistica: "Logistica", magazzino: "Magazzino", dedicato: "Dedicato" };
const urgencyLabels = { normale: "Normale", urgente: "Urgente", programmata: "Programmata" };

function formatEstimateRange(estimate) {
  if (!estimate) return null;
  const min = Number(estimate.price_min);
  const max = Number(estimate.price_max);
  const exact = Number(estimate.price_exact);
  if (Number.isFinite(min) && Number.isFinite(max)) return `${min.toFixed(0)}-${max.toFixed(0)}`;
  if (Number.isFinite(exact)) return `${exact.toFixed(0)}`;
  return null;
}

export default function AdminQuotes() {
  const { data: quotes, reload } = useAdminData("quotes");
  const [filter, setFilter] = useState("tutti");
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const updateStatus = async (id, status) => {
    if (status === "accettato") {
      const code = window.prompt("Inserisci il Codice Cliente per accettare il preventivo:");
      if (!code) { toast.error("Codice cliente obbligatorio per accettare"); return; }
      try {
        const res = await axios.put(`${API}/admin/quotes/${id}/status`, { status, client_code: code.trim() }, authHeaders());
        if (res.data.shipment_created) {
          toast.success(`Preventivo accettato! Spedizione ${res.data.tracking_number} creata`);
        } else {
          toast.success("Stato aggiornato");
        }
        reload();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
      }
      return;
    }
    try {
      await axios.put(`${API}/admin/quotes/${id}/status`, { status }, authHeaders());
      toast.success("Stato aggiornato");
      reload();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditForm({
      name: q.name || "", company: q.company || "", email: q.email || "", phone: q.phone || "",
      service_type: q.service_type || "trasporto", origin: q.origin || "", destination: q.destination || "",
      weight: q.weight || "", dimensions: q.dimensions || "", numero_colli: q.numero_colli || "",
      tipo_imballo: q.tipo_imballo || "", description: q.description || "", urgency: q.urgency || "normale",
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/quotes/${editingId}`, editForm, authHeaders());
      toast.success("Preventivo aggiornato");
      setEditingId(null);
      reload();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Eliminare questo preventivo?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, authHeaders());
      toast.success("Preventivo eliminato");
      reload();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filtered = filter === "tutti" ? quotes : quotes.filter((q) => q.status === filter);

  const inputCls = "h-9 px-3 border border-zinc-200 rounded-sm text-sm w-full focus:border-[#FF5200] outline-none";
  const labelCls = "block text-[10px] font-bold tracking-widest uppercase text-zinc-400 mb-1 font-heading";

  return (
    <div data-testid="admin-quotes">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("tutti")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-colors ${filter === "tutti" ? "bg-[#09090B] text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
          data-testid="quotes-filter-tutti">
          Tutti ({quotes.length})
        </button>
        {statusOptions.map((s) => {
          const count = quotes.filter((q) => q.status === s.value).length;
          return (
            <button key={s.value} onClick={() => setFilter(s.value)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-colors ${filter === s.value ? "bg-[#09090B] text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
              data-testid={`quotes-filter-${s.value}`}>
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-left" data-testid="quotes-table">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Data</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Cliente</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Servizio</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Tratta</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Stima</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Stato</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">Nessun preventivo</td></tr>
            ) : (
              filtered.map((q) => (
                <React.Fragment key={q.id}>
                  <tr className="hover:bg-zinc-50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === q.id ? null : q.id)} data-testid={`quote-row-${q.id}`}>
                    <td className="px-4 py-4 text-sm text-zinc-500 whitespace-nowrap">
                      {new Date(q.created_at).toLocaleDateString("it-IT")}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-bold text-[#09090B]">{q.name}</p>
                      <p className="text-xs text-zinc-500">{q.company || q.email}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#09090B]">{serviceLabels[q.service_type] || q.service_type}</p>
                      <p className="text-xs text-zinc-500">{urgencyLabels[q.urgency] || q.urgency}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-600">
                      {q.origin && q.destination ? `${q.origin} → ${q.destination}` : "N/A"}
                      {q.weight && <span className="text-zinc-400 ml-1">({q.weight})</span>}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {formatEstimateRange(q.estimate) ? (
                        <span className="font-bold text-[#FF5200]" data-testid={`quote-estimate-${q.id}`}>{formatEstimateRange(q.estimate)} EUR</span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <select value={q.status} onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-sm border-0 cursor-pointer ${statusOptions.find((s) => s.value === q.status)?.style || "bg-zinc-100 text-zinc-700"}`}
                        data-testid={`quote-status-${q.id}`}>
                        {statusOptions.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); startEdit(q); }} className="p-1.5 text-zinc-400 hover:text-[#FF5200]" title="Modifica" data-testid={`edit-quote-${q.id}`}><Edit size={14} /></button>
                        <button onClick={(e) => { e.stopPropagation(); deleteQuote(q.id); }} className="p-1.5 text-zinc-400 hover:text-red-500" title="Elimina" data-testid={`delete-quote-${q.id}`}><Trash2 size={14} /></button>
                        {expandedRow === q.id ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                      </div>
                    </td>
                  </tr>
                  {expandedRow === q.id && (
                    <tr><td colSpan={7} className="bg-zinc-50 px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Contatto</p>
                          <p className="text-sm font-bold">{q.name}</p>
                          {q.company && <p className="text-xs text-zinc-600">{q.company}</p>}
                          <p className="text-xs text-zinc-600">{q.email}</p>
                          <p className="text-xs text-zinc-600">{q.phone}</p>
                        </div>
                        <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Dettagli Merce</p>
                          {q.numero_colli && <p className="text-xs"><strong>N. Colli:</strong> {q.numero_colli}</p>}
                          {q.tipo_imballo && <p className="text-xs"><strong>Imballo:</strong> {q.tipo_imballo}</p>}
                          {q.weight && <p className="text-xs"><strong>Peso:</strong> {q.weight}</p>}
                          {q.dimensions && <p className="text-xs"><strong>Dimensioni:</strong> {q.dimensions}</p>}
                        </div>
                        <div className="bg-white p-3 border border-zinc-200 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Descrizione</p>
                          <p className="text-xs text-zinc-600">{q.description || "Nessuna descrizione"}</p>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="quote-edit-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200">
              <h3 className="text-sm font-bold tracking-widest uppercase font-heading">Modifica Preventivo</h3>
              <button onClick={() => setEditingId(null)} className="p-1 text-zinc-400 hover:text-zinc-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Nome *</label>
                  <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls} data-testid="quote-edit-name" />
                </div>
                <div>
                  <label className={labelCls}>Azienda</label>
                  <input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className={inputCls} data-testid="quote-edit-company" />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={inputCls} data-testid="quote-edit-email" />
                </div>
                <div>
                  <label className={labelCls}>Telefono</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={inputCls} data-testid="quote-edit-phone" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Origine</label>
                  <input value={editForm.origin} onChange={(e) => setEditForm({ ...editForm, origin: e.target.value })} className={inputCls} data-testid="quote-edit-origin" />
                </div>
                <div>
                  <label className={labelCls}>Destinazione</label>
                  <input value={editForm.destination} onChange={(e) => setEditForm({ ...editForm, destination: e.target.value })} className={inputCls} data-testid="quote-edit-destination" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className={labelCls}>N. Colli</label>
                  <input value={editForm.numero_colli} onChange={(e) => setEditForm({ ...editForm, numero_colli: e.target.value })} className={inputCls} data-testid="quote-edit-colli" />
                </div>
                <div>
                  <label className={labelCls}>Tipo Imballo</label>
                  <select value={editForm.tipo_imballo} onChange={(e) => setEditForm({ ...editForm, tipo_imballo: e.target.value })} className={`${inputCls} bg-white`} data-testid="quote-edit-imballo">
                    <option value="">-</option>
                    <option value="collo">Collo</option>
                    <option value="pallet">Pallet</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Peso</label>
                  <input value={editForm.weight} onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })} className={inputCls} placeholder="es. 500 kg" data-testid="quote-edit-weight" />
                </div>
                <div>
                  <label className={labelCls}>Dimensioni</label>
                  <input value={editForm.dimensions} onChange={(e) => setEditForm({ ...editForm, dimensions: e.target.value })} className={inputCls} placeholder="LxPxH" data-testid="quote-edit-dimensions" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Servizio</label>
                  <select value={editForm.service_type} onChange={(e) => setEditForm({ ...editForm, service_type: e.target.value })} className={`${inputCls} bg-white`} data-testid="quote-edit-service">
                    <option value="trasporto">Trasporto</option>
                    <option value="logistica">Logistica</option>
                    <option value="magazzino">Magazzino</option>
                    <option value="dedicato">Dedicato</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Urgenza</label>
                  <select value={editForm.urgency} onChange={(e) => setEditForm({ ...editForm, urgency: e.target.value })} className={`${inputCls} bg-white`} data-testid="quote-edit-urgency">
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="programmata">Programmata</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Descrizione</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm resize-none h-20 focus:border-[#FF5200] outline-none" data-testid="quote-edit-description" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button onClick={() => setEditingId(null)} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-800">Annulla</button>
              <button onClick={saveEdit} className="px-6 py-2 bg-[#FF5200] text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e04800]" data-testid="quote-save-btn">
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
